import { useEffect, useMemo, useState } from 'react';
import { Users, Search, Mail, Phone, Calendar } from 'lucide-react';
import { userService } from '../../services/userService';
import { UI, ui } from '../../utils/ui';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    userService.getMyCustomers()
      .then((res) => setCustomers(res.data.customers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return customers.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q));
  }, [customers, search]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 16 };
  const titleStyle = { fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text };
  const subtitleStyle = { margin: '8px 0 0', fontSize: 14, color: UI.colors.muted };

  const pillStyle = {
    padding: '6px 12px',
    borderRadius: 9999,
    backgroundColor: UI.colors.primary,
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  };

  const searchWrapStyle = {
    maxWidth: 520,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 14,
    border: `1.5px solid ${UI.colors.border}`,
    backgroundColor: '#ffffff',
  };

  const fieldCore = {
    width: '100%',
    border: 'none',
    outline: 'none',
    fontSize: 14,
    backgroundColor: 'transparent',
    color: UI.colors.text,
  };

  const activePill = (active) => ({
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 900,
    border: `1px solid ${active ? '#86efac' : '#fecaca'}`,
    backgroundColor: active ? '#dcfce7' : '#fee2e2',
    color: active ? '#166534' : '#991b1b',
  });

  return (
    <div style={wrapStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={titleStyle}>My Customers</h1>
          <p style={subtitleStyle}>Customers who booked through you</p>
        </div>
        <span style={pillStyle}>
          {filtered.length} customer{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Search */}
      <div style={searchWrapStyle}>
        <Search size={16} color="#9ca3af" />
        <input type="text" placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} style={fieldCore} />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div style={{ ...ui.card, textAlign: 'center', padding: '56px 18px' }}>
          <Users size={48} color="#9ca3af" style={{ opacity: 0.25, margin: '0 auto 12px' }} />
          <p style={{ margin: 0, color: UI.colors.muted, fontSize: 16, fontWeight: 900 }}>No customers found</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map((c) => (
            <div key={c.id} style={ui.card}>
              <div style={{ padding: 18 }}>
                {/* Avatar Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14, marginBottom: 14, borderBottom: `1px solid ${UI.colors.border}` }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: UI.colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#ffffff', fontWeight: 900, fontSize: 18 }}>{(c.name || '?')[0].toUpperCase()}</span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 900, color: UI.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</p>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: UI.colors.muted, fontWeight: 700 }}>Customer</p>
                  </div>
                </div>

                {/* Details */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: UI.colors.muted, fontSize: 13 }}>
                    <Mail size={14} color={UI.colors.primary} />
                    <span style={{ color: UI.colors.text, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</span>
                  </div>
                  {c.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: UI.colors.muted, fontSize: 13 }}>
                      <Phone size={14} color={UI.colors.primary} />
                      <span style={{ color: UI.colors.text, fontWeight: 700 }}>{c.phone}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: UI.colors.muted, fontSize: 13 }}>
                    <Calendar size={14} color={UI.colors.primary} />
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>Joined {new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 14, borderTop: `1px solid ${UI.colors.border}` }}>
                  <span style={{ fontSize: 13, fontWeight: 900, color: UI.colors.text }}>
                    {c.booking_count || 0} booking{c.booking_count !== 1 ? 's' : ''}
                  </span>
                  <span style={activePill(Boolean(c.is_active))}>{c.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Customers;

